import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { BrandCodeType } from 'utils/globalTypes';
import Spin from 'components/Spin';

/* Terms of service pages */
const PrivacyShieldTermsOfService = lazy(
  () => import('pages/terms-of-service/privacy-shield'),
);
const MissGroupSafeTermsOfService = lazy(
  () => import('pages/terms-of-service/miss-group-safe'),
);

/* Privacy policy pages */
const PrivacyShieldPrivacyPolicy = lazy(
  () => import('pages/privacy-policy/privacy-shield'),
);

type BrandConfigMapping = Record<
  BrandCodeType,
  {
    privacyPolicy: string | (() => ReactNode);
    termsOfService: string | (() => ReactNode);
    vaultProject: string;
    adaChat: boolean;
    accountDetails: boolean;
    secureUpload: boolean;
    plan: boolean;
    security: boolean;
    missingPII: boolean;
    finishSetup: boolean;
    darkwebDashboardCard: boolean;
    defaultBillingCategoryKey: string;
    downloadsRecommended: boolean;
    copyRightText: string;
    hasCustomerServiceSupport: boolean;
    horizontalVaultDashboardCards: boolean;
    ssoRedirectURL: string | null;
    dwEmailVerification: {
      adaChat: boolean;
      signInButton: boolean;
      supportAt: string | null;
      availability: string | null;
    };
    supportPage: {
      pageTitle: {
        active: boolean;
        title?: string;
      };
      customerSupportTitle: {
        active: boolean;
        title?: string;
        availability?: ReactNode;
      };
      faq: {
        active: boolean;
      };
      pages: {
        title: string;
        href: string;
        icon: string;
        description?: string;
        customerSupport?: boolean;
      }[];
      call: {
        active: boolean;
        phoneNumber?: string;
        availability?: ReactNode;
        customerSupport?: boolean;
      };
      mail: {
        active: boolean;
        recipientName?: string;
        address?: ReactNode;
        customerSupport?: boolean;
      };
      email: {
        active: boolean;
        address?: string;
        customerSupport?: boolean;
      };
    };
  }
>;

/* Brand configuration mappings */
export const BRAND_CODES = {
  privacyShield: 'privacy',
  privacyMedicalShield: 'psms',
  missGroupSafe: 'mgsafe',
  domeneShop: 'dshop',
  domainkeskusSafe: 'dsafe',
  partyhat: 'partyhat',
  protectIV: 'protectiv',
  phSafe: 'phsafe',
} as const;

export const BRAND_CONFIG_MAPPING: BrandConfigMapping = {
  [BRAND_CODES.privacyShield]: {
    privacyPolicy: () => (
      <Suspense fallback={<Spin />}>
        <PrivacyShieldPrivacyPolicy />
      </Suspense>
    ),
    termsOfService: () => (
      <Suspense fallback={<Spin />}>
        <PrivacyShieldTermsOfService />
      </Suspense>
    ),
    vaultProject: 'privacy_shield',
    adaChat: true,
    accountDetails: true,
    secureUpload: false,
    plan: true,
    security: true,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: true,
    copyRightText: 'CyEx, LLC. All rights reserved.',
    hasCustomerServiceSupport: true,
    horizontalVaultDashboardCards: false,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: true,
      signInButton: true,
      supportAt: null,
      availability:
        'Representatives are available Monday - Friday, 8AM-11PM ET and Saturday, 9AM-6PM ET.',
    },
    supportPage: {
      pageTitle: {
        active: true,
        title: 'More ways to reach us',
      },
      customerSupportTitle: {
        active: true,
        title: 'You can reach our customer care team:',
        availability:
          'Customer care representatives are available Monday - Friday, 8AM-11PM ET and Saturday, 9AM-6PM ET.',
      },
      faq: {
        active: true,
      },
      pages: [],
      call: {
        active: true,
        customerSupport: true,
        availability: (
          <>
            Monday - Friday, 8AM-11PM ET <br /> Saturday, 9AM-6PM ET
          </>
        ),
      },
      mail: {
        active: true,
        address: 'PO Box 52330, Boston, MA 02210',
        customerSupport: false,
      },
      email: {
        active: true,
        customerSupport: true,
      },
    },
  },
  [BRAND_CODES.privacyMedicalShield]: {
    privacyPolicy: 'policy/privacy-medical-shield-policy.md',
    termsOfService: 'policy/tou/privacy-medical-shield-terms.md',
    vaultProject: 'privacy_medical_shield',
    adaChat: true,
    accountDetails: true,
    secureUpload: true,
    plan: true,
    security: true,
    missingPII: true,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: true,
    copyRightText: 'CyEx, LLC. All rights reserved.',
    hasCustomerServiceSupport: true,
    horizontalVaultDashboardCards: false,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: true,
      signInButton: true,
      supportAt: null,
      availability:
        'Representatives are available Monday - Friday, 8AM-11PM ET and Saturday, 9AM-6PM ET.',
    },
    supportPage: {
      pageTitle: {
        active: true,
        title: 'More ways to reach us',
      },
      customerSupportTitle: {
        active: true,
        title: 'You can reach our customer care team:',
        availability:
          'Customer care representatives are available Monday - Friday, 8AM-11PM ET and Saturday, 9AM-6PM ET.',
      },
      faq: {
        active: true,
      },
      pages: [],
      call: {
        active: true,
        customerSupport: true,
        availability: (
          <>
            Monday - Friday, 8AM-11PM ET <br /> Saturday, 9AM-6PM ET
          </>
        ),
      },
      mail: {
        active: true,
        address: 'PO Box 52330, Boston, MA 02210',
        customerSupport: false,
      },
      email: {
        active: true,
        customerSupport: true,
      },
    },
  },
  [BRAND_CODES.missGroupSafe]: {
    privacyPolicy: 'policy/mgsafe-policy.md',
    termsOfService: () => (
      <Suspense fallback={<Spin />}>
        <MissGroupSafeTermsOfService />
      </Suspense>
    ),
    vaultProject: 'eyeon',
    adaChat: false,
    accountDetails: true,
    secureUpload: false,
    plan: false,
    security: false,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: false,
    copyRightText: 'Miss Group Safe. All rights reserved.',
    hasCustomerServiceSupport: false,
    horizontalVaultDashboardCards: true,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: false,
      signInButton: true,
      supportAt: null,
      availability: null,
    },
    supportPage: {
      pageTitle: {
        active: false,
      },
      customerSupportTitle: {
        active: false,
      },
      faq: {
        active: true,
      },
      pages: [
        {
          title: 'Miss Hosting SE/NO:',
          href: 'https://misshosting.help',
          icon: 'support-faq',
          customerSupport: true,
        },
        {
          title: 'Domene:',
          href: 'https://www.domene.no/kontakt',
          icon: 'support-faq',
          customerSupport: true,
        },
        {
          title: 'Hemsida24:',
          href: 'https://hemsida24.help/sv',
          icon: 'support-faq',
          customerSupport: true,
        },
      ],
      call: {
        active: false,
      },
      mail: {
        active: false,
      },
      email: {
        active: false,
      },
    },
  },
  [BRAND_CODES.domeneShop]: {
    privacyPolicy: 'policy/dshop-policy.md',
    termsOfService: () => (
      <Suspense fallback={<Spin />}>
        <MissGroupSafeTermsOfService />
      </Suspense>
    ),
    vaultProject: 'domene_shop',
    adaChat: false,
    accountDetails: true,
    secureUpload: false,
    plan: false,
    security: false,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: false,
    copyRightText: 'Domeneshop. All rights reserved.',
    hasCustomerServiceSupport: false,
    horizontalVaultDashboardCards: true,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: false,
      signInButton: true,
      supportAt: null,
      availability: null,
    },
    supportPage: {
      pageTitle: {
        active: false,
      },
      customerSupportTitle: {
        active: false,
      },
      faq: {
        active: true,
      },
      pages: [
        {
          title: 'Domene:',
          href: 'https://domene.shop/contact',
          icon: 'support-faq',
          customerSupport: true,
        },
      ],
      call: {
        active: false,
      },
      mail: {
        active: false,
      },
      email: {
        active: false,
      },
    },
  },
  [BRAND_CODES.domainkeskusSafe]: {
    privacyPolicy: 'policy/dshop-policy.md',
    termsOfService: () => (
      <Suspense fallback={<Spin />}>
        <MissGroupSafeTermsOfService />
      </Suspense>
    ),
    vaultProject: 'dsafe',
    adaChat: false,
    accountDetails: true,
    secureUpload: false,
    plan: false,
    security: false,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: false,
    copyRightText: 'Domainkeskus Safe. All rights reserved.',
    hasCustomerServiceSupport: false,
    horizontalVaultDashboardCards: true,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: false,
      signInButton: true,
      supportAt: null,
      availability: null,
    },
    supportPage: {
      pageTitle: {
        active: false,
      },
      customerSupportTitle: {
        active: false,
      },
      faq: {
        active: true,
      },
      pages: [
        {
          title: 'Support page',
          href: 'https://help.domainkeskus.com',
          icon: 'support-faq',
          customerSupport: false,
        },
      ],
      call: {
        active: false,
      },
      mail: {
        active: false,
      },
      email: {
        active: true,
        address: 'info@domainkeskus.com',
        customerSupport: true,
      },
    },
  },
  [BRAND_CODES.partyhat]: {
    privacyPolicy: 'https://www.getpartyhat.com/privacy',
    termsOfService: 'https://www.getpartyhat.com/terms',
    vaultProject: 'partyhat',
    adaChat: true,
    accountDetails: false,
    secureUpload: false,
    plan: false,
    security: false,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: true,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: false,
    copyRightText: 'Partyhat Inc. All rights reserved.',
    hasCustomerServiceSupport: true,
    horizontalVaultDashboardCards: false,
    ssoRedirectURL: 'https://www.getpartyhat.com',
    dwEmailVerification: {
      adaChat: false,
      signInButton: false,
      supportAt: 'support@getpartyhat.com',
      availability: null,
    },
    supportPage: {
      pageTitle: {
        active: true,
        title: 'More ways to reach us',
      },
      customerSupportTitle: {
        active: false,
      },
      faq: {
        active: true,
      },
      pages: [],
      call: {
        active: false,
      },
      mail: {
        active: false,
      },
      email: {
        active: true,
        address: 'support@getpartyhat.com',
        customerSupport: true,
      },
    },
  },
  [BRAND_CODES.protectIV]: {
    privacyPolicy: 'https://www.iv.lt/privatumo-politika',
    termsOfService: 'https://www.iv.lt/bendrosios-paslaugu-teikimo-salygos',
    vaultProject: 'protectiv',
    adaChat: false,
    accountDetails: true,
    secureUpload: false,
    plan: false,
    security: false,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: false,
    copyRightText: 'UAB „Interneto vizija. All rights reserved.',
    hasCustomerServiceSupport: false,
    horizontalVaultDashboardCards: true,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: false,
      signInButton: true,
      supportAt: null,
      availability: null,
    },
    supportPage: {
      pageTitle: {
        active: false,
      },
      customerSupportTitle: {
        active: false,
      },
      faq: {
        active: true,
      },
      pages: [
        {
          title: 'Support page',
          href: 'https://pagalba.iv.lt/lt',
          icon: 'support-faq',
          customerSupport: false,
        },
      ],
      call: {
        active: false,
      },
      mail: {
        active: false,
      },
      email: {
        active: true,
        address: 'info@iv.lt',
        customerSupport: true,
      },
    },
  },
  [BRAND_CODES.phSafe]: {
    privacyPolicy: 'https://www.profesionalhosting.com/contratos',
    termsOfService: 'https://www.profesionalhosting.com/contratos',
    vaultProject: 'phsafe',
    adaChat: false,
    accountDetails: true,
    secureUpload: false,
    plan: false,
    security: false,
    missingPII: false,
    finishSetup: false,
    darkwebDashboardCard: false,
    defaultBillingCategoryKey: 'privacy_breach',
    downloadsRecommended: false,
    copyRightText: 'Soluciones Web On Line SL. All rights reserved.',
    hasCustomerServiceSupport: false,
    horizontalVaultDashboardCards: true,
    ssoRedirectURL: null,
    dwEmailVerification: {
      adaChat: false,
      signInButton: true,
      supportAt: null,
      availability: null,
    },
    supportPage: {
      pageTitle: {
        active: false,
      },
      customerSupportTitle: {
        active: false,
      },
      faq: {
        active: true,
      },
      pages: [
        {
          title: 'Professional Hosting',
          description: 'Visit our knowledge base',
          href: 'https://kb.profesionalhosting.com',
          icon: 'support-faq',
          customerSupport: false,
        },
      ],
      call: {
        active: false,
      },
      mail: {
        active: false,
      },
      email: {
        active: true,
        address: 'soporte@profesionalhosting.es',
        customerSupport: true,
      },
    },
  },
};
